import type { InteractableDef, InteractableType, InteractionVerb, WorldPos } from "../core/types";

/**
 * Puente TILED → mundo del juego.
 *
 * Tiled sabe DÓNDE está cada cosa (posición) y QUÉ clave visual tiene.
 * Nuestro código sabe QUÉ HACE cada cosa (misión, riesgo, diálogo), que se
 * resuelve por el `id`/`ref`. Este módulo solo traduce el JSON de Tiled a
 * nuestras estructuras; NO contiene lógica de gameplay.
 *
 * Convención de coordenadas (mapa isométrico de Tiled):
 *   un objeto en el tile (a,b) se guarda en x = a * tileheight, y = b * tileheight.
 *   Por eso tx = x / tileheight y ty = y / tileheight. Al arrastrar un objeto en
 *   Tiled cambia su x/y y, por tanto, su tile — sin tocar TypeScript.
 *   (Se puede forzar el tile con las propiedades personalizadas `tx`/`ty`.)
 */

// ---- forma mínima del JSON de Tiled que nos interesa ----
export interface TiledProperty {
  name: string;
  type?: string;
  value: string | number | boolean;
}

export interface TiledObject {
  id?: number;
  name?: string;
  type?: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
  properties?: TiledProperty[];
}

export interface TiledLayer {
  type: string; // "objectgroup" | "tilelayer" | ...
  name: string;
  objects?: TiledObject[];
  data?: number[]; // tilelayer: gids por celda
  width?: number;
  height?: number;
  visible?: boolean;
}

export interface TiledTileset {
  firstgid: number;
  name?: string;
  image?: string;
  imagewidth?: number;
  imageheight?: number;
  tilewidth?: number;
  tileheight?: number;
  columns?: number;
}

export interface TiledMap {
  orientation?: string;
  width: number;
  height: number;
  tilewidth: number;
  tileheight: number;
  layers: TiledLayer[];
  tilesets?: TiledTileset[];
  properties?: TiledProperty[];
}

// ---- resultado ya traducido a nuestro mundo ----
export interface DecorPlacement {
  visual: string;
  pos: WorldPos;
}

export interface CollisionRect {
  /** Esquina en tiles y tamaño en tiles (footprint, NO el sprite). */
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Capa de suelo con tileset real (opcional). Si falta, suelo procedural. */
export interface GroundLayer {
  imagePath: string;
  tileW: number;
  tileH: number;
  columns: number;
  firstgid: number;
  data: number[];
  w: number;
  h: number;
}

export interface LoadedMap {
  grid: { w: number; h: number };
  tileSize: { w: number; h: number };
  spawn: WorldPos;
  /** Interactivos listos para el motor (WalkChapter). Posición desde Tiled. */
  interactions: InteractableDef[];
  /** Decoración pura (sin gameplay): clave visual + posición. */
  decor: DecorPlacement[];
  /** Footprints de colisión sólida en tiles. */
  collisions: CollisionRect[];
  /** Suelo pintado con tileset real (si el mapa lo trae). */
  ground?: GroundLayer;
}

function propMap(o: TiledObject): Record<string, string | number | boolean> {
  const m: Record<string, string | number | boolean> = {};
  for (const p of o.properties ?? []) m[p.name] = p.value;
  return m;
}

function layer(map: TiledMap, name: string): TiledLayer | undefined {
  return map.layers.find((l) => l.name === name);
}

/** Convierte un objeto de Tiled a coordenadas de tile de nuestro mundo. */
export function objectToTile(o: TiledObject, tileHeight: number): WorldPos {
  const p = propMap(o);
  const tx = typeof p.tx === "number" ? p.tx : o.x / tileHeight;
  const ty = typeof p.ty === "number" ? p.ty : o.y / tileHeight;
  return { x: tx, y: ty };
}

/**
 * Parsea un mapa de Tiled (JSON) a nuestro `LoadedMap`. Función pura y
 * determinista (testeable sin Phaser). Los nombres de capa esperados:
 *   PLAYER_SPAWN, INTERACTIONS, DECOR (o PROPS), COLLISION.
 */
export function parseTiledMap(map: TiledMap): LoadedMap {
  const th = map.tileheight;

  // --- spawn del jugador ---
  let spawn: WorldPos = { x: Math.floor(map.width / 2), y: Math.floor(map.height / 2) };
  const spawnLayer = layer(map, "PLAYER_SPAWN");
  const spawnObj = spawnLayer?.objects?.[0];
  if (spawnObj) spawn = objectToTile(spawnObj, th);

  // --- interactivos (gameplay) ---
  const interactions: InteractableDef[] = [];
  for (const o of layer(map, "INTERACTIONS")?.objects ?? []) {
    const p = propMap(o);
    const id = String(p.id ?? o.name ?? `obj_${o.id ?? interactions.length}`);
    const def: InteractableDef = {
      id,
      type: (String(p.type ?? o.type ?? "object") as InteractableType),
      pos: objectToTile(o, th),
      label: String(p.label ?? o.name ?? id),
      verb: p.verb ? (String(p.verb) as InteractionVerb) : undefined,
      ref: p.ref != null ? String(p.ref) : undefined,
    };
    if (typeof p.radius === "number") def.radius = p.radius;
    if (p.visual != null) def.visual = String(p.visual);
    interactions.push(def);
  }

  // --- decoración (sin gameplay) ---
  const decor: DecorPlacement[] = [];
  const decorLayer = layer(map, "DECOR") ?? layer(map, "PROPS");
  for (const o of decorLayer?.objects ?? []) {
    const p = propMap(o);
    const visual = String(p.visual ?? o.name ?? "");
    if (!visual) continue;
    decor.push({ visual, pos: objectToTile(o, th) });
  }

  // --- colisiones sólidas ---
  const collisions: CollisionRect[] = [];
  for (const o of layer(map, "COLLISION")?.objects ?? []) {
    const tile = objectToTile(o, th);
    collisions.push({
      x: tile.x,
      y: tile.y,
      w: o.width ? o.width / th : 1,
      h: o.height ? o.height / th : 1,
    });
  }

  // --- suelo con tileset real (opcional) ---
  let ground: GroundLayer | undefined;
  const groundLayer = layer(map, "GROUND");
  const ts = map.tilesets?.[0];
  if (groundLayer?.data && ts?.image) {
    ground = {
      imagePath: ts.image,
      tileW: ts.tilewidth ?? map.tilewidth,
      tileH: ts.tileheight ?? map.tileheight,
      columns: ts.columns ?? 1,
      firstgid: ts.firstgid ?? 1,
      data: groundLayer.data,
      w: groundLayer.width ?? map.width,
      h: groundLayer.height ?? map.height,
    };
  }

  return {
    grid: { w: map.width, h: map.height },
    tileSize: { w: map.tilewidth, h: map.tileheight },
    spawn,
    interactions,
    decor,
    collisions,
    ground,
  };
}
